// 1.5.16: Phase 4 - 会话服务（conversations + conversation_messages 表）
// 替代 queryHistoryService 中的会话管理职责

import { supabase, getAnonymousUserId, getUserId } from '../lib/supabase';
import type { ChatMessage } from '../../features/chat/types/chat';
import type { Restaurant } from '../../features/search/types/restaurant';
import { getCurrentUser } from './authService';
import { logger } from '../utils/logger';

// ============================================================
// Types
// ============================================================

export interface Conversation {
  id: string;
  user_id: string | null;
  auth_user_id: string | null;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message_at: string | null;
  metadata: Record<string, unknown> | null;
  legacy_query_history_id: string | null;
}

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  sequence: number;
  status: string;
  image_url: string | null;
  restaurants: Restaurant[] | null;
  restaurant_reasons: string[] | null;
  search_query: string | null;
  recommendation_data: Record<string, unknown> | null;
}

export interface ConversationWithMessages extends Conversation {
  messages: ConversationMessage[];
}

// ============================================================
// Create
// ============================================================

/**
 * Create a new conversation and insert its messages.
 * Returns the conversation with all messages.
 */
export async function createConversation(
  messages: ChatMessage[],
  metadata?: Record<string, unknown> | null,
): Promise<{ conversation: ConversationWithMessages | null; error: string | null }> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();

    if (!userId) {
      return { conversation: null, error: '用户 ID 缺失' };
    }

    // Derive title from first user message
    const firstUserMsg = messages.find((m) => m.role === 'user');
    const title = firstUserMsg ? firstUserMsg.content.slice(0, 50) : '';

    const insertData: Record<string, unknown> = {
      title,
      metadata: metadata ?? null,
    };

    if (isAuthenticated && user) {
      insertData.auth_user_id = user.id;
      insertData.user_id = getAnonymousUserId();
    } else {
      insertData.user_id = userId;
      insertData.auth_user_id = null;
    }

    // Insert conversation
    const { data: conv, error: convErr } = await supabase
      .from('conversations')
      .insert(insertData)
      .select()
      .single();

    if (convErr || !conv) {
      logger.error('❌ Failed to create conversation:', convErr);
      return { conversation: null, error: convErr?.message ?? '创建会话失败' };
    }

    // Insert messages
    const msgRows = messages
      .filter((m) => m.content || m.imageUrl)
      .map((m, idx) => chatMessageToRow(conv.id, m, idx));

    if (msgRows.length > 0) {
      const { error: msgErr } = await supabase
        .from('conversation_messages')
        .insert(msgRows);

      if (msgErr) {
        logger.error('❌ Failed to insert conversation messages:', msgErr);
        // conversation created but messages failed – return partial
      }
    }

    // Fetch the full conversation with messages
    const full = await getConversationWithMessages(conv.id);
    return { conversation: full, error: null };
  } catch (err: any) {
    logger.error('❌ createConversation exception:', err);
    return { conversation: null, error: err.message };
  }
}

// ============================================================
// Update (append messages to existing conversation)
// ============================================================

/**
 * Append new messages to an existing conversation.
 * Messages with a `sequence` >= existingCount are inserted.
 */
export async function updateConversation(
  conversationId: string,
  allMessages: ChatMessage[],
  metadata?: Record<string, unknown> | null,
): Promise<{ conversation: ConversationWithMessages | null; error: string | null }> {
  try {
    // Get existing message count
    const { count, error: countErr } = await supabase
      .from('conversation_messages')
      .select('id', { count: 'exact', head: true })
      .eq('conversation_id', conversationId);

    if (countErr) {
      logger.error('❌ Failed to count messages:', countErr);
      return { conversation: null, error: countErr.message };
    }

    const existingCount = count ?? 0;

    // Only insert messages that are new (sequence >= existingCount)
    const newMessages = allMessages.slice(existingCount);
    if (newMessages.length > 0) {
      const msgRows = newMessages.map((m, idx) =>
        chatMessageToRow(conversationId, m, existingCount + idx),
      );

      const { error: insertErr } = await supabase
        .from('conversation_messages')
        .insert(msgRows);

      if (insertErr) {
        logger.error('❌ Failed to append messages:', insertErr);
        return { conversation: null, error: insertErr.message };
      }
    }

    // Update conversation metadata if provided
    if (metadata) {
      await supabase
        .from('conversations')
        .update({ metadata })
        .eq('id', conversationId);
    }

    const full = await getConversationWithMessages(conversationId);
    return { conversation: full, error: null };
  } catch (err: any) {
    logger.error('❌ updateConversation exception:', err);
    return { conversation: null, error: err.message };
  }
}

/**
 * Save or update a conversation.
 * - If conversationId is provided, appends new messages.
 * - Otherwise, creates a new conversation.
 * Returns the conversation ID.
 */
export async function saveConversation(
  messages: ChatMessage[],
  metadata?: Record<string, unknown> | null,
  conversationId?: string | null,
): Promise<{ id: string | null; error: string | null }> {
  if (conversationId) {
    const { conversation, error } = await updateConversation(conversationId, messages, metadata);
    return { id: conversation?.id ?? conversationId, error };
  }
  const { conversation, error } = await createConversation(messages, metadata);
  return { id: conversation?.id ?? null, error };
}

// ============================================================
// Read
// ============================================================

/**
 * List conversations for the current user (lightweight – no messages).
 */
export async function listConversations(
  limit = 50,
): Promise<Conversation[]> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();

    let query = supabase
      .from('conversations')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (isAuthenticated && user) {
      query = query.eq('auth_user_id', user.id);
    } else {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('❌ Failed to list conversations:', error);
      return [];
    }

    return (data ?? []) as Conversation[];
  } catch (err: any) {
    logger.error('❌ listConversations exception:', err);
    return [];
  }
}

/**
 * Get a single conversation with all its messages.
 */
export async function getConversationWithMessages(
  conversationId: string,
): Promise<ConversationWithMessages | null> {
  try {
    const { data: conv, error: convErr } = await supabase
      .from('conversations')
      .select('*')
      .eq('id', conversationId)
      .single();

    if (convErr || !conv) {
      logger.error('❌ Failed to get conversation:', convErr);
      return null;
    }

    const { data: msgs, error: msgErr } = await supabase
      .from('conversation_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('sequence', { ascending: true });

    if (msgErr) {
      logger.error('❌ Failed to get conversation messages:', msgErr);
      return null;
    }

    return {
      ...(conv as Conversation),
      messages: (msgs ?? []) as ConversationMessage[],
    };
  } catch (err: any) {
    logger.error('❌ getConversationWithMessages exception:', err);
    return null;
  }
}

// ============================================================
// Delete
// ============================================================

/**
 * Delete a conversation and all its messages (CASCADE).
 */
export async function deleteConversation(conversationId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId);

    if (error) {
      logger.error('❌ Failed to delete conversation:', error);
      return false;
    }
    return true;
  } catch (err: any) {
    logger.error('❌ deleteConversation exception:', err);
    return false;
  }
}

/**
 * Delete all conversations for the current user.
 */
export async function clearAllConversations(): Promise<boolean> {
  try {
    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();

    let query = supabase.from('conversations').delete();

    if (isAuthenticated && user) {
      query = query.eq('auth_user_id', user.id);
    } else {
      query = query.eq('user_id', userId);
    }

    const { error } = await query;

    if (error) {
      logger.error('❌ Failed to clear conversations:', error);
      return false;
    }
    return true;
  } catch (err: any) {
    logger.error('❌ clearAllConversations exception:', err);
    return false;
  }
}

// ============================================================
// Conversion helpers
// ============================================================

/**
 * Convert a ChatMessage to a conversation_messages row.
 */
function chatMessageToRow(
  conversationId: string,
  msg: ChatMessage,
  sequence: number,
): Record<string, unknown> {
  return {
    conversation_id: conversationId,
    role: msg.role,
    content: msg.content ?? '',
    created_at: msg.timestamp
      ? new Date(msg.timestamp).toISOString()
      : new Date().toISOString(),
    sequence,
    status: msg.status ?? 'completed',
    image_url: msg.imageUrl ?? null,
    restaurants:
      msg.restaurants && msg.restaurants.length > 0 ? msg.restaurants : null,
    restaurant_reasons:
      msg.restaurantReasons && msg.restaurantReasons.length > 0
        ? msg.restaurantReasons
        : null,
    search_query: msg.searchQuery ?? null,
    recommendation_data: msg.recommendationData ?? null,
  };
}

/**
 * Convert a ConversationMessage row back to a ChatMessage.
 */
export function rowToChatMessage(row: ConversationMessage): ChatMessage {
  return {
    id: row.id,
    role: row.role,
    content: row.content,
    timestamp: new Date(row.created_at).getTime(),
    status: (row.status as ChatMessage['status']) ?? 'completed',
    imageUrl: row.image_url ?? undefined,
    restaurants: (row.restaurants as Restaurant[]) ?? undefined,
    restaurantReasons: (row.restaurant_reasons as string[]) ?? undefined,
    searchQuery: row.search_query ?? undefined,
    recommendationData: row.recommendation_data as any,
  };
}
